import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Hook para mover un modal arrastrándolo desde su título/cabecera.
 * Solo mueve el modal (transform translate); no cambia tamaño ni cierre.
 *
 * Uso:
 *   const drag = useDraggableModal(visor.open);
 *   <div style={drag.panelStyle}>            // panel del modal
 *     <div {...drag.handleProps}>Título</div> // zona de agarre
 *     <iframe className={drag.dragging ? 'pointer-events-none' : ''} ... />
 */
export default function useDraggableModal(open) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const start = useRef({ x: 0, y: 0, baseX: 0, baseY: 0 });
  const movedRef = useRef(false);

  // Al reabrir el modal vuelve a su posición original (centrado)
  useEffect(() => {
    if (open) setPos({ x: 0, y: 0 });
  }, [open]);

  const onPointerDown = useCallback((e) => {
    // Solo botón principal; ignorar clicks sobre botones/links dentro de la cabecera
    if (e.button !== 0 || e.target.closest('button, a, input, select')) return;
    e.preventDefault();
    movedRef.current = false;
    start.current = { x: e.clientX, y: e.clientY, baseX: pos.x, baseY: pos.y };
    setDragging(true);
  }, [pos]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = e.clientX - start.current.x;
      const dy = e.clientY - start.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
      setPos({ x: start.current.baseX + dx, y: start.current.baseY + dy });
    };
    const onUp = () => {
      setDragging(false);
      // Si hubo arrastre real, tragar el click que se dispara al soltar
      // (evita que el overlay lo interprete como "cerrar modal")
      if (movedRef.current) {
        const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        window.addEventListener('click', swallow, { capture: true, once: true });
        // Limpieza por si no llega a dispararse ningún click
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 300);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging]);

  return {
    dragging,
    panelStyle: { transform: `translate(${pos.x}px, ${pos.y}px)` },
    handleProps: {
      onPointerDown,
      style: { cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' },
      title: 'Arrastrá para mover',
    },
  };
}
